import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useLocation, useNavigate } from "react-router-dom";
import type { AdminApi } from "../api/client.js";
import type { CalendarConfig } from "../api/types.js";
import { Card } from "../components/Card.js";
import { ErrorBanner } from "../components/ErrorBanner.js";
import { Field } from "../components/Field.js";
import { Select } from "../components/Select.js";

// US-centric IANA timezones, ordered roughly east-to-west so the
// list reads like a continental sweep. Add others as needed; the
// underlying input still accepts any IANA string via "Other".
const TIMEZONE_OPTIONS = [
  { value: "America/New_York",    label: "Eastern (America/New_York)" },
  { value: "America/Chicago",     label: "Central (America/Chicago)" },
  { value: "America/Denver",      label: "Mountain (America/Denver)" },
  { value: "America/Phoenix",     label: "Mountain — no DST (America/Phoenix)" },
  { value: "America/Los_Angeles", label: "Pacific (America/Los_Angeles)" },
  { value: "America/Anchorage",   label: "Alaska (America/Anchorage)" },
  { value: "Pacific/Honolulu",    label: "Hawaii (Pacific/Honolulu)" },
  { value: "UTC",                 label: "UTC" },
];

const Schema = z.object({
  practice_timezone: z.string().min(1, "Required"),
  business_hours_json: z.string().min(1, "Required"),
  slot_granularity_minutes: z.coerce.number().int().positive(),
  min_lead_time_hours: z.coerce.number().int().min(0),
  max_lead_time_days: z.coerce.number().int().positive(),
});

type FormValues = z.infer<typeof Schema>;

const DEFAULT_HOURS = JSON.stringify(
  {
    mon: [["09:00", "17:00"]],
    tue: [["09:00", "17:00"]],
    wed: [["09:00", "17:00"]],
    thu: [["09:00", "17:00"]],
    fri: [["09:00", "17:00"]],
    sat: [],
    sun: [],
  },
  null,
  2,
);

type Props = { api: AdminApi };

export function CalendarConfigPage({ api }: Props) {
  const [saved, setSaved] = useState(false);
  const q = useQuery({
    queryKey: ["admin", "calendar-config"],
    queryFn: () => api.getCalendarConfig(),
  });
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormValues>({ resolver: zodResolver(Schema) });

  useEffect(() => {
    if (q.data) {
      reset({
        practice_timezone: q.data.practice_timezone,
        business_hours_json: JSON.stringify(q.data.business_hours, null, 2),
        slot_granularity_minutes: q.data.slot_granularity_minutes,
        min_lead_time_hours: q.data.min_lead_time_hours,
        max_lead_time_days: q.data.max_lead_time_days,
      });
    } else if (!q.isPending) {
      reset({
        practice_timezone: "",
        business_hours_json: DEFAULT_HOURS,
        slot_granularity_minutes: 15,
        min_lead_time_hours: 24,
        max_lead_time_days: 60,
      });
    }
  }, [q.data, q.isPending, reset]);

  const submit = useMutation({
    mutationFn: async (v: FormValues) => {
      let business_hours: CalendarConfig["business_hours"];
      try {
        business_hours = JSON.parse(
          v.business_hours_json,
        ) as CalendarConfig["business_hours"];
      } catch {
        throw new Error("Business hours must be valid JSON");
      }
      return api.putCalendarConfig({
        practice_timezone: v.practice_timezone,
        business_hours,
        slot_granularity_minutes: v.slot_granularity_minutes,
        min_lead_time_hours: v.min_lead_time_hours,
        max_lead_time_days: v.max_lead_time_days,
      });
    },
    onSuccess: () => setSaved(true),
  });

  return (
    <div className="grid gap-4">
      <GoogleConnectionPanel api={api} />
      <BusyCalendarsPanel api={api} />

      <Card title="Schedule configuration">
        {q.isError ? (
          <ErrorBanner message="Could not load configuration." />
        ) : null}
        {submit.isError ? (
          <ErrorBanner message={(submit.error as Error).message} />
        ) : null}
        {saved ? (
          <p className="mb-4 rounded-md bg-emerald-50 p-3 text-sm text-emerald-800">
            Saved.
          </p>
        ) : null}
        <form
          onSubmit={handleSubmit((v) => {
            setSaved(false);
            submit.mutate(v);
          })}
          className="grid gap-4"
        >
          <Select
            label="Practice timezone"
            required
            options={TIMEZONE_OPTIONS}
            {...register("practice_timezone")}
            error={errors.practice_timezone?.message}
          />
          <div>
            <label
              htmlFor="hours"
              className="block text-sm font-medium text-slate-700"
            >
              Business hours (JSON)
              <span className="ml-1 text-rose-600">*</span>
            </label>
            <textarea
              id="hours"
              rows={10}
              className="field-input mt-1 font-mono text-xs"
              {...register("business_hours_json")}
            />
            {errors.business_hours_json ? (
              <p className="mt-1 text-xs text-rose-600">
                {errors.business_hours_json.message}
              </p>
            ) : null}
          </div>
          <div className="grid gap-4 sm:grid-cols-3">
            <Field
              label="Slot granularity (min)"
              type="number"
              required
              {...register("slot_granularity_minutes")}
              error={errors.slot_granularity_minutes?.message}
            />
            <Field
              label="Min lead time (hours)"
              type="number"
              required
              {...register("min_lead_time_hours")}
              error={errors.min_lead_time_hours?.message}
            />
            <Field
              label="Max lead time (days)"
              type="number"
              required
              {...register("max_lead_time_days")}
              error={errors.max_lead_time_days?.message}
            />
          </div>
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={submit.isPending}
              className="rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
            >
              {submit.isPending ? "Saving…" : "Save"}
            </button>
          </div>
        </form>
      </Card>
    </div>
  );
}

function GoogleConnectionPanel({ api }: { api: AdminApi }) {
  const queryClient = useQueryClient();
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  const flash = params.get("google");
  const flashReason = params.get("reason");

  const status = useQuery({
    queryKey: ["admin", "google-status"],
    queryFn: () => api.getGoogleStatus(),
  });

  useEffect(() => {
    if (flash === "connected") {
      void queryClient.invalidateQueries({
        queryKey: ["admin", "google-status"],
      });
      navigate(location.pathname, { replace: true });
    }
  }, [flash, queryClient, navigate, location.pathname]);

  const start = useMutation({
    mutationFn: async () => api.startGoogleOAuth(),
    onSuccess: (res) => {
      window.location.href = res.url;
    },
  });

  const disconnect = useMutation({
    mutationFn: async () => api.disconnectGoogle(),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["admin", "google-status"],
      });
      void queryClient.invalidateQueries({
        queryKey: ["admin", "busy-calendars"],
      });
    },
  });

  const data = status.data;

  return (
    <Card title="Google Calendar connection">
      {flash === "connected" ? (
        <p className="mb-3 rounded-md bg-emerald-50 p-2 text-sm text-emerald-800">
          Google Calendar connected. Pick which calendars to use below.
        </p>
      ) : null}
      {flash === "error" ? (
        <ErrorBanner
          message={`Google connection failed${flashReason ? `: ${flashReason}` : ""}.`}
        />
      ) : null}

      {status.isPending ? (
        <p className="text-sm text-slate-500">Checking connection status…</p>
      ) : data?.connected ? (
        <div className="grid gap-2 text-sm">
          <p>
            Connected as{" "}
            <span className="font-medium">{data.connected_by_email}</span>
            {data.connected_at ? (
              <>
                {" "}
                on{" "}
                <span className="font-medium">
                  {new Date(data.connected_at).toLocaleString()}
                </span>
              </>
            ) : null}
            .
          </p>
          <div>
            <button
              type="button"
              onClick={() => disconnect.mutate()}
              disabled={disconnect.isPending}
              className="mt-2 rounded-md border border-slate-300 px-3 py-1.5 text-sm hover:bg-slate-50"
            >
              {disconnect.isPending ? "Disconnecting…" : "Disconnect"}
            </button>
          </div>
        </div>
      ) : (
        <div className="grid gap-2">
          <p className="text-sm text-slate-600">
            Not connected. Connect to authorize Google Calendar access; you
            can then pick which calendars feed availability.
          </p>
          <div>
            <button
              type="button"
              onClick={() => start.mutate()}
              disabled={start.isPending}
              className="rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
            >
              {start.isPending ? "Redirecting…" : "Connect Google Calendar"}
            </button>
          </div>
        </div>
      )}
    </Card>
  );
}

function BusyCalendarsPanel({ api }: { api: AdminApi }) {
  const queryClient = useQueryClient();
  const status = useQuery({
    queryKey: ["admin", "google-status"],
    queryFn: () => api.getGoogleStatus(),
  });
  const connected = status.data?.connected ?? false;

  const available = useQuery({
    enabled: connected,
    queryKey: ["admin", "available-calendars"],
    queryFn: () => api.listAvailableCalendars(),
  });
  const busy = useQuery({
    enabled: connected,
    queryKey: ["admin", "busy-calendars"],
    queryFn: () => api.getBusyCalendars(),
  });

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [initialised, setInitialised] = useState(false);

  useEffect(() => {
    if (!initialised && busy.data) {
      setSelected(new Set(busy.data.calendars.map((c) => c.calendar_id)));
      setInitialised(true);
    }
  }, [busy.data, initialised]);

  const save = useMutation({
    mutationFn: async () => api.putBusyCalendars([...selected]),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["admin", "busy-calendars"],
      });
    },
  });

  if (!connected) {
    return (
      <Card title="Busy calendars">
        <p className="text-sm text-slate-600">
          Connect Google Calendar first to choose which calendars feed
          availability.
        </p>
      </Card>
    );
  }

  if (available.isPending || busy.isPending) {
    return (
      <Card title="Busy calendars">
        <p className="text-sm text-slate-600">Loading your calendars…</p>
      </Card>
    );
  }

  if (available.isError) {
    return (
      <Card title="Busy calendars">
        <ErrorBanner message="Could not list calendars from Google." />
      </Card>
    );
  }

  const rows = available.data?.calendars ?? [];
  const currentSet = new Set(
    busy.data?.calendars.map((c) => c.calendar_id) ?? [],
  );
  const dirty =
    rows.some((r) => selected.has(r.id) && !currentSet.has(r.id)) ||
    [...currentSet].some((id) => !selected.has(id));

  return (
    <Card
      title="Busy calendars"
      action={
        <button
          type="button"
          disabled={!dirty || save.isPending}
          onClick={() => save.mutate()}
          className="rounded-md bg-blue-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
        >
          {save.isPending ? "Saving…" : "Save selection"}
        </button>
      }
    >
      <p className="mb-3 text-sm text-slate-600">
        Events on any selected calendar block availability. Adding a
        calendar runs an initial sync (a few seconds); removing one tears
        down its push subscription and drops its cached events.
      </p>
      {save.isError ? (
        <ErrorBanner
          message={
            (save.error as Error | undefined)?.message ||
            "Could not update selection."
          }
        />
      ) : null}
      <ul className="grid gap-2">
        {rows.map((c) => {
          const isSelected = selected.has(c.id);
          const meta = busy.data?.calendars.find((b) => b.calendar_id === c.id);
          return (
            <li
              key={c.id}
              className="flex items-start gap-3 rounded-md border border-slate-200 bg-white p-3"
            >
              <input
                type="checkbox"
                checked={isSelected}
                onChange={(e) => {
                  setSelected((prev) => {
                    const next = new Set(prev);
                    if (e.target.checked) next.add(c.id);
                    else next.delete(c.id);
                    return next;
                  });
                }}
                className="mt-1 h-4 w-4 rounded border-slate-300 text-blue-600"
              />
              <div className="grow text-sm">
                <p className="font-medium text-slate-900">
                  {c.summary}
                  {c.primary ? (
                    <span className="ml-2 text-xs text-slate-500">
                      (primary)
                    </span>
                  ) : null}
                </p>
                <p className="text-xs text-slate-500">{c.id}</p>
                {meta ? (
                  <p className="mt-1 text-xs text-slate-500">
                    {meta.initial_backfill_done
                      ? "Synced"
                      : "Initial sync running…"}
                    {meta.channel_expiration
                      ? ` • channel renews around ${new Date(meta.channel_expiration).toLocaleDateString()}`
                      : null}
                  </p>
                ) : null}
              </div>
            </li>
          );
        })}
      </ul>
    </Card>
  );
}
